/**
 * Onboarding Wizard
 * Step-by-step setup for new businesses
 */

import React, { useState } from 'react';
import { Card, Button, Input, Checkbox } from './ui';
import { useOnboarding } from '../contexts/OnboardingContext';
import { IndustryTemplateSelector } from './IndustryTemplateSelector';
import { TierConfigurationEditor } from './TierConfigurationEditor';

interface OnboardingStep {
  id: number;
  title: string;
  description: string;
  icon: string;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 1,
    title: 'Business Profile',
    description: 'Tell us about your business',
    icon: '🏪',
  },
  {
    id: 2,
    title: 'Rewards Setup',
    description: 'Configure your loyalty rewards',
    icon: '🎁',
  },
  {
    id: 3,
    title: 'Sustainability Bonuses',
    description: 'Set eco-friendly multipliers',
    icon: '🌱',
  },
  {
    id: 4,
    title: 'Add Location',
    description: 'Set up your first location',
    icon: '📍',
  },
  {
    id: 5,
    title: 'Launch!',
    description: "You're ready to go",
    icon: '🚀',
  },
];

interface OnboardingWizardProps {
  onComplete?: () => void;
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { data, updateData, nextStep: _onboardingNextStep, previousStep: _onboardingPreviousStep, currentStep: onboardingStep } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(onboardingStep || 1);
  const [formData, setFormData] = useState({
    // Step 1: Business Profile
    businessName: '',
    category: '',
    phoneNumber: '',

    // Step 2: Rewards
    b3trPerDollar: 0.5,
    minPurchase: 0,
    redemptionRate: 100,

    // Step 3: Sustainability
    ecosystemBonusEnabled: true,
    mugshotMultiplier: 1.5,
    greencartMultiplier: 1.5,
    bybMultiplier: 1.5,

    // Step 4: Location
    locationName: '',
    address: '',
    city: '',
    zipCode: '',
  });

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <IndustryTemplateSelector />;

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🎁</div>
              <h2 className="text-h2 font-bold text-black mb-2">Configure Rewards & Tiers</h2>
              <p className="text-base text-black-lighter">
                Set earning rates and customize your tier progression
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-black mb-2">Earning Rate</label>
              <Input
                type="number"
                label="Stars Per Dollar Spent"
                value={data.b3trPerDollar}
                onChange={(e) => updateData({ b3trPerDollar: parseFloat(e.target.value) || 10 })}
                step="1"
                min="1"
                helperText={`Customers earn ${data.b3trPerDollar * 10} Stars on a $10 purchase`}
              />
            </div>

            <div>
              <Input
                type="number"
                label="Minimum Redemption (Stars)"
                value={data.minRedemption}
                onChange={(e) => updateData({ minRedemption: parseFloat(e.target.value) || 100 })}
                step="50"
                min="50"
                helperText={`Customers need at least ${data.minRedemption} Stars to redeem rewards`}
              />
            </div>

            <div className="pt-4">
              <h3 className="text-base font-semibold text-black mb-3">Tier Configuration</h3>
              <TierConfigurationEditor
                initialConfig={{
                  bronze: { threshold: data.tierThresholds.bronze, multiplier: data.tierMultipliers.bronze, name: 'Bronze' },
                  silver: { threshold: data.tierThresholds.silver, multiplier: data.tierMultipliers.silver, name: 'Silver' },
                  gold: { threshold: data.tierThresholds.gold, multiplier: data.tierMultipliers.gold, name: 'Gold' },
                  platinum: { threshold: data.tierThresholds.platinum, multiplier: data.tierMultipliers.platinum, name: 'Platinum' },
                }}
                onChange={(tierConfig) => {
                  updateData({
                    tierThresholds: {
                      bronze: tierConfig.bronze.threshold,
                      silver: tierConfig.silver.threshold,
                      gold: tierConfig.gold.threshold,
                      platinum: tierConfig.platinum.threshold,
                    },
                    tierMultipliers: {
                      bronze: tierConfig.bronze.multiplier,
                      silver: tierConfig.silver.multiplier,
                      gold: tierConfig.gold.multiplier,
                      platinum: tierConfig.platinum.multiplier,
                    },
                  });
                }}
                showPresets={true}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🌱</div>
              <h2 className="text-h2 font-bold text-black mb-2">Bonus Star Multipliers</h2>
              <p className="text-base text-black-lighter">
                Optional: give extra Stars on specific promotions or partner offers
              </p>
            </div>

            <Card>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-black mb-1">Enable Sustainability Bonuses</h4>
                    <p className="text-sm text-black-lighter">
                      Customers using Mugshot, Greencart, or BYB get extra Stars
                    </p>
                  </div>
                  <Checkbox
                    checked={formData.ecosystemBonusEnabled}
                    onChange={(e) =>
                      setFormData({ ...formData, ecosystemBonusEnabled: e.target.checked })
                    }
                  />
                </div>
              </div>
            </Card>

            {formData.ecosystemBonusEnabled && (
              <>
                <Card>
                  <div className="p-5 bg-cream-50">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-3xl">☕</span>
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-black">Mugshot (Reusable Cups)</h4>
                        <p className="text-xs text-black-lighter mt-0.5">
                          Saves 10g plastic & 55g CO₂ per use
                        </p>
                      </div>
                    </div>
                    <Input
                      type="number"
                      label="Multiplier"
                      value={formData.mugshotMultiplier}
                      onChange={(e) =>
                        setFormData({ ...formData, mugshotMultiplier: parseFloat(e.target.value) })
                      }
                      step="0.1"
                      min="1.0"
                      max="5.0"
                      helperText={`${formData.mugshotMultiplier}x rewards for Mugshot users`}
                    />
                  </div>
                </Card>

                <Card>
                  <div className="p-5 bg-cream-50">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-3xl">🛒</span>
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-black">Greencart (Sustainable Shopping)</h4>
                        <p className="text-xs text-black-lighter mt-0.5">
                          Saves 50g plastic & 200g CO₂ per transaction
                        </p>
                      </div>
                    </div>
                    <Input
                      type="number"
                      label="Multiplier"
                      value={formData.greencartMultiplier}
                      onChange={(e) =>
                        setFormData({ ...formData, greencartMultiplier: parseFloat(e.target.value) })
                      }
                      step="0.1"
                      min="1.0"
                      max="5.0"
                      helperText={`${formData.greencartMultiplier}x rewards for Greencart users`}
                    />
                  </div>
                </Card>

                <Card>
                  <div className="p-5 bg-cream-50">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-3xl">👜</span>
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-black">BYB (Reusable Bags)</h4>
                        <p className="text-xs text-black-lighter mt-0.5">
                          Saves 8g plastic & 33g CO₂ per use
                        </p>
                      </div>
                    </div>
                    <Input
                      type="number"
                      label="Multiplier"
                      value={formData.bybMultiplier}
                      onChange={(e) =>
                        setFormData({ ...formData, bybMultiplier: parseFloat(e.target.value) })
                      }
                      step="0.1"
                      min="1.0"
                      max="5.0"
                      helperText={`${formData.bybMultiplier}x rewards for BYB users`}
                    />
                  </div>
                </Card>

                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-900">
                    <strong>Total Multiplier:</strong> Customers using all 3 apps get{' '}
                    <strong>
                      {(formData.mugshotMultiplier *
                        formData.greencartMultiplier *
                        formData.bybMultiplier).toFixed(2)}
                      x
                    </strong>{' '}
                    rewards!
                  </p>
                </div>
              </>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">📍</div>
              <h2 className="text-h2 font-bold text-black mb-2">Add Your First Location</h2>
              <p className="text-base text-black-lighter">
                We'll generate a QR code for customer check-ins
              </p>
            </div>

            <Input
              label="Location Name"
              value={formData.locationName}
              onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
              placeholder="e.g., Main Street Location"
              isRequired
            />

            <Input
              label="Street Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main St"
              isRequired
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="San Francisco"
                isRequired
              />

              <Input
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                placeholder="94102"
                isRequired
              />
            </div>

            <Card>
              <div className="p-6 bg-blue-50 border-l-4 border-blue-400">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✨</span>
                  <div>
                    <h4 className="text-base font-semibold text-black mb-1">What happens next?</h4>
                    <p className="text-sm text-black-lighter">
                      We'll generate a unique QR code for this location. Print it and display it
                      prominently. When customers scan it, they'll check in and automatically earn
                      Stars on their purchases!
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-h2 font-bold text-black mb-2">You're All Set!</h2>
              <p className="text-base text-black-lighter">
                Your Buffables Rewards program is ready to launch
              </p>
            </div>

            <Card>
              <div className="p-6">
                <h3 className="text-h4 font-semibold text-black mb-4">What You've Created</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <span className="text-green-600 text-xl">✓</span>
                    <div>
                      <p className="text-sm font-semibold text-black">Business Profile</p>
                      <p className="text-xs text-black-lighter">{formData.businessName}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <span className="text-green-600 text-xl">✓</span>
                    <div>
                      <p className="text-sm font-semibold text-black">Rewards Program</p>
                      <p className="text-xs text-black-lighter">
                        {formData.b3trPerDollar} Stars per $1 spent
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <span className="text-green-600 text-xl">✓</span>
                    <div>
                      <p className="text-sm font-semibold text-black">Sustainability Bonuses</p>
                      <p className="text-xs text-black-lighter">
                        {formData.ecosystemBonusEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <span className="text-green-600 text-xl">✓</span>
                    <div>
                      <p className="text-sm font-semibold text-black">Location</p>
                      <p className="text-xs text-black-lighter">{formData.locationName}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6 bg-orange-50">
                <h4 className="text-base font-semibold text-black mb-3 flex items-center gap-2">
                  <span>🚀</span>
                  <span>Next Steps</span>
                </h4>
                <ol className="space-y-2 text-sm text-black-lighter">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-orange-600">1.</span>
                    <span>Download your location's QR code from the Locations page</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-orange-600">2.</span>
                    <span>Print and display the QR code prominently at your location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-orange-600">3.</span>
                    <span>Tell customers to scan the code when they visit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-orange-600">4.</span>
                    <span>Watch your loyalty program grow!</span>
                  </li>
                </ol>
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {ONBOARDING_STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                      transition-all duration-300
                      ${
                        currentStep > step.id
                          ? 'bg-green-500 text-white'
                          : currentStep === step.id
                          ? 'bg-orange-400 text-white'
                          : 'bg-cream-200 text-black-lighter'
                      }
                    `}
                  >
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <span className="text-xs mt-2 text-black-lighter hidden sm:block">
                    {step.title}
                  </span>
                </div>
                {index < ONBOARDING_STEPS.length - 1 && (
                  <div className="flex-1 h-1 mx-2 bg-cream-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        currentStep > step.id ? 'bg-green-500 w-full' : 'bg-cream-200 w-0'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card>
          <div className="p-8">{renderStepContent()}</div>

          {/* Navigation Buttons */}
          <div className="p-6 border-t border-border-light flex items-center justify-between gap-4">
            {currentStep > 1 && currentStep < ONBOARDING_STEPS.length && (
              <Button variant="secondary" size="md" onClick={handleBack}>
                ← Back
              </Button>
            )}

            <div className="flex-1" />

            <Button
              variant="primary"
              size="md"
              onClick={handleNext}
              className="min-w-35"
            >
              {currentStep === ONBOARDING_STEPS.length ? 'Launch Dashboard →' : 'Continue →'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingWizard;
